import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatCurrency, formatNumber } from '@/lib/format';
import {
  fromInputValue,
  payrollFor,
  toInputValue,
  useCreateTimesheet,
  useDeleteTimesheet,
  useUpdateTimesheet,
  type TimesheetEntry,
} from './timesheets.api';
import type { TimesheetType } from './timesheet-types.api';
import type { StaffProfile } from '@/features/staff/staff.api';

/**
 * One shift, created or corrected.
 *
 * An open shift is normal — somebody clocked in and has not clocked out — so
 * the end time may be left blank, and the figures below simply wait for it.
 */
export function TimesheetEntrySheet({
  entry,
  businessId,
  staff,
  types,
  payroll,
  open,
  onOpenChange,
}: {
  /** An entry corrects it; `'new'` creates one. */
  entry: TimesheetEntry | 'new' | null;
  businessId: string | null;
  staff: StaffProfile[];
  types: TimesheetType[];
  /** Shown only while the page is in payroll mode, with its rate and cut. */
  payroll: { on: boolean; hourlyRate: number; deductionPercent: number };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';

  const create = useCreateTimesheet();
  const update = useUpdateTimesheet();
  const remove = useDeleteTimesheet();

  const existing = entry !== 'new' ? entry : null;

  const [draft, setDraft] = useState({
    employeeId: '',
    startTime: '',
    endTime: '',
    typeId: '',
    comment: '',
  });
  const [hydrated, setHydrated] = useState<string | null>(null);

  const key = existing?.id ?? (entry === 'new' ? 'new' : null);
  if (entry && hydrated !== key) {
    setHydrated(key);
    setDraft({
      employeeId: existing?.employeeId ?? '',
      startTime: toInputValue(existing?.startTime),
      endTime: toInputValue(existing?.endTime),
      typeId:
        existing?.employeeTimeSheetTypeId
        ?? types.find((type) => type.isDefault)?.id
        ?? types[0]?.id
        ?? '',
      comment: existing?.comment ?? '',
    });
  }

  if (!entry) return null;

  const set = (patch: Partial<typeof draft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const busy = create.isPending || update.isPending || remove.isPending;
  const failed = () => toast.error(t('timesheets.actionFailed'));

  // Hours are computed here rather than read back, so the figures move as the
  // times are edited instead of only after a save.
  const hours =
    draft.startTime && draft.endTime
      ? Math.max(
          0,
          (new Date(`${draft.endTime}:00Z`).getTime()
            - new Date(`${draft.startTime}:00Z`).getTime())
            / 3_600_000
        )
      : null;

  const money =
    hours == null
      ? null
      : payrollFor(hours, 0, payroll.hourlyRate, payroll.deductionPercent);

  const complete =
    draft.startTime !== '' && (existing !== null || draft.employeeId !== '');

  function save() {
    if (existing) {
      update.mutate(
        {
          timeSheetId: existing.id,
          startTime: fromInputValue(draft.startTime)!,
          endTime: fromInputValue(draft.endTime),
          employeeTimeSheetTypeId: draft.typeId || null,
          comment: draft.comment || null,
        },
        {
          onSuccess: () => {
            toast.success(t('timesheets.saved'));
            onOpenChange(false);
          },
          onError: failed,
        }
      );
      return;
    }

    create.mutate(
      {
        employeeId: draft.employeeId,
        businessId: businessId!,
        startTime: fromInputValue(draft.startTime)!,
        endTime: fromInputValue(draft.endTime),
        employeeTimeSheetTypeId: draft.typeId || null,
        comment: draft.comment || null,
      },
      {
        onSuccess: () => {
          toast.success(t('timesheets.created'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {existing ? t('timesheets.editEntry') : t('timesheets.newEntry')}
          </SheetTitle>
          {existing?.employee && (
            <p className="text-muted-foreground truncate text-sm">
              {existing.employee.completeName}
            </p>
          )}
        </SheetHeader>

        <div className="space-y-3 p-4">
          {!existing && (
            <div className="space-y-1.5">
              <Label>{t('timesheets.employee')}</Label>
              <Select
                value={draft.employeeId}
                disabled={busy}
                onValueChange={(employeeId) => set({ employeeId })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('timesheets.pickEmployee')} />
                </SelectTrigger>
                <SelectContent>
                  {staff
                    .filter((person) => person.userId)
                    .map((person) => (
                      <SelectItem key={person.id} value={person.userId!}>
                        {person.completeName}
                        {!person.isActive && ` (${t('timesheets.inactive')})`}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t('timesheets.startTime')}</Label>
            <Input
              type="datetime-local"
              value={draft.startTime}
              disabled={busy}
              onChange={(event) => set({ startTime: event.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('timesheets.endTime')}</Label>
            <Input
              type="datetime-local"
              value={draft.endTime}
              disabled={busy}
              onChange={(event) => set({ endTime: event.target.value })}
            />
            <p className="text-muted-foreground text-xs">
              {t('timesheets.endTimeHint')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>{t('timesheets.type')}</Label>
            <Select
              value={draft.typeId}
              disabled={busy || types.length === 0}
              onValueChange={(typeId) => set({ typeId })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('timesheets.pickType')} />
              </SelectTrigger>
              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {isEnglish ? type.nameEn : type.nameEs}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>{t('timesheets.comment')}</Label>
            <Input
              value={draft.comment}
              disabled={busy}
              onChange={(event) => set({ comment: event.target.value })}
            />
          </div>

          <div className="divide-y rounded-xl border">
            <Figure
              label={t('timesheets.hours')}
              value={hours == null ? '—' : formatNumber(hours)}
            />
            {payroll.on && (
              <>
                <Figure
                  label={t('timesheets.gross')}
                  value={money ? formatCurrency(money.gross) : '—'}
                />
                <Figure
                  label={t('timesheets.deduction')}
                  value={money ? formatCurrency(money.deduction) : '—'}
                />
                <Figure
                  label={t('timesheets.net')}
                  value={money ? formatCurrency(money.net) : '—'}
                />
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            {existing ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={busy}>
                    {remove.isPending && <Loader2 className="animate-spin" />}
                    {t('timesheets.delete')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('timesheets.delete')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('timesheets.deletePrompt')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() =>
                        remove.mutate(existing.id, {
                          onSuccess: () => {
                            toast.success(t('timesheets.deleted'));
                            onOpenChange(false);
                          },
                          onError: failed,
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <span />
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button disabled={busy || !complete} onClick={save}>
                {(create.isPending || update.isPending) && (
                  <Loader2 className="animate-spin" />
                )}
                {existing ? t('common.save') : t('timesheets.create')}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}
